import Api from "@/configurations/Api";
import RequestService from "@/configurations/AxiosConfig";
import { ICreateShiftPatternTimeRequest, IExportShiftsToExcelRequest, IExportShiftsToPdfRequest, IGetShiftPatternTimesRequest, IGetShiftReportRequest, IRemoveShiftPatternTimeRequest, IShiftRequest, IUpdateShiftPatternTimeRequest } from "@/types/type";
import { toast } from "sonner";

const endpoints = {
  shifts: 'shifts',
  byCompanyId: 'shifts/byCompanyId',
};

const createShiftReportQuery = (data: IGetShiftReportRequest) => {
  const params = new URLSearchParams();

  if (data.company_id !== undefined) {
    params.set('company_id', String(data.company_id));
  }
  if (data.start_date) {
    params.set('start_date', data.start_date instanceof Date ? data.start_date.toISOString() : data.start_date);
  }
  if (data.end_date) {
    params.set('end_date', data.end_date instanceof Date ? data.end_date.toISOString() : data.end_date);
  }
  if (data.user_id !== undefined) {
    params.set('user_id', String(data.user_id));
  }
  if (data.branch_id !== undefined) {
    params.set('branch_id', String(data.branch_id));
  }
  if (data.status?.length) {
    data.status.forEach(status => params.append('status', status));
  }

  return params.toString();
};

const downloadShiftReport = async (endpoint: string, data: IGetShiftReportRequest, fileName: string) => {
  const query = createShiftReportQuery(data);
  const response = await fetch(`${Api.getBaseUrl()}shifts/${endpoint}${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: {
      'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
  toast.success("Dosya başarıyla indirildi.");
};

const ShiftsService = {
  getAllShifts: async () => RequestService.get(endpoints.shifts),
  getShiftsByCompanyId: async (companyId: string | number) => RequestService.get(`${endpoints.byCompanyId}/${companyId}`),
  updateShift: async (id: string, data: IShiftRequest) => RequestService.patch(`${endpoints.shifts}/${id}`, data),
  createShift: async (data: IShiftRequest) => RequestService.post(endpoints.shifts, data),
  deleteShift: async (id: string | number) => RequestService.delete(`${endpoints.shifts}/${id}`),
  getShiftsByShiftId: async (shiftId: string | number) => RequestService.get(`${endpoints.shifts}/${shiftId}`),
  getShiftsByUserId: async (userId: string | number) => RequestService.get(`${endpoints.shifts}/getUserShift/${userId}`),
  checkIfShiftExistsForMonth: async (data: { branch_id: number | null, year: number, month: number }) => 
    RequestService.post(`${endpoints.shifts}/checkIfShiftExistsForMonth`, data, { showLoader: false }),
  createShiftPatternTime: async (data: ICreateShiftPatternTimeRequest) => RequestService.post(`${endpoints.shifts}/createShiftPatternTime`, data),
  getShiftPatternTimes: async (data: IGetShiftPatternTimesRequest) => RequestService.post(`${endpoints.shifts}/getShiftPatternTimes`, data),
  removeShiftPatternTime: async (data: IRemoveShiftPatternTimeRequest) => RequestService.post(`${endpoints.shifts}/removeShiftPatternTime`, data),
  updateShiftPatternTime: async (data: IUpdateShiftPatternTimeRequest) => RequestService.post(`${endpoints.shifts}/updateShiftPatternTime`, data),
  getShiftTemplates: async () => RequestService.get(`${endpoints.shifts}/getShiftTemplates`),
  getShiftReport: async (data: IGetShiftReportRequest) => {
    const query = createShiftReportQuery(data);
    return RequestService.get(`${endpoints.shifts}/report${query ? `?${query}` : ''}`);
  },
  exportShiftReportToExcel: async (data: IGetShiftReportRequest) => downloadShiftReport('exportShiftReportToExcel', data, 'shift_report.xlsx'),
  exportShiftReportToPdf: async (data: IGetShiftReportRequest) => downloadShiftReport('exportShiftReportToPdf', data, 'shift_report.pdf'),
  exportShiftsToExcel: async (data: IExportShiftsToExcelRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}shifts/exportShiftsToExcel`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()
  
    if (!blob.type.includes("spreadsheet") && !blob.type.includes("excel")) {
      console.warn("Response might not be an Excel file:", blob.type)
    }
  
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'shifts.xlsx'
  
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  
    window.URL.revokeObjectURL(downloadUrl)
  
    toast.success("Dosya başarıyla indirildi.")
  },
  exportShiftsToPdf: async (data: IExportShiftsToPdfRequest) => {
    const response = await fetch(`${Api.getBaseUrl()}shifts/exportShiftsPdf`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        'Authorization': `${RequestService.defaults.headers.common.Authorization}`,
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  
    const blob = await response.blob()
  
    if (!blob.type.includes("spreadsheet") && !blob.type.includes("pdf")) {
      console.warn("Response might not be a PDF file:", blob.type)
    }
  
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = 'shifts.pdf'
  
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  
    window.URL.revokeObjectURL(downloadUrl)
  
    toast.success("Dosya başarıyla indirildi.")
  }
};

export default ShiftsService;
